/**
 * dxfExport.js — minimal DXF R12 export (no external deps)
 *
 * Exports: walls (LINE entities), openings (BLOCK inserts),
 * annotations (TEXT entities), manual dims (DIMENSION entities),
 * symbols (POINT/TEXT entities), layers.
 *
 * DXF R12 is universally supported by AutoCAD, QCAD, FreeCAD, LibreCAD.
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mm2unit = v => (v / 1000).toFixed(4); // mm → metres (standard DXF unit)

function esc(str) {
  return String(str || "").replace(/[^\x20-\x7E]/g, "?");
}

// ─── Section builders ─────────────────────────────────────────────────────────

function headerSection() {
  return [
    "  0\nSECTION",
    "  2\nHEADER",
    "  9\n$ACADVER",
    "  1\nAC1009",
    "  9\n$INSUNITS",
    " 70\n4",   // 4 = millimetres
    "  9\n$MEASUREMENT",
    " 70\n1",   // metric
    "  0\nENDSEC",
  ].join("\n");
}

function layerDef(id, name, color, lineweight) {
  // ACI color codes: 1=red,2=yellow,3=green,4=cyan,5=blue,6=magenta,7=white
  const aci = color ? colorToACI(color) : 7;
  return [
    "  0\nLAYER",
    `  2\n${esc(id)}`,
    " 70\n0",
    ` 62\n${aci}`,
    "  6\nCONTINUOUS",
  ].join("\n");
}

function colorToACI(hex) {
  // Map hex color to nearest AutoCAD Color Index (very rough)
  if (!hex || hex.length < 6) return 7;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (r > 200 && g < 80 && b < 80) return 1;   // red
  if (r > 200 && g > 200 && b < 80) return 2;   // yellow
  if (r < 80 && g > 200 && b < 80) return 3;    // green
  if (r < 80 && g > 200 && b > 200) return 4;   // cyan
  if (r < 80 && g < 80 && b > 200) return 5;    // blue
  if (r > 200 && g < 80 && b > 200) return 6;   // magenta
  if (r > 150 && g > 150 && b > 150) return 7;  // white/grey
  return 7;
}

function tablesSection(layers) {
  const layerEntries = Object.values(layers).map(l =>
    layerDef(l.id, l.name, l.color, 0)
  ).join("\n");

  return [
    "  0\nSECTION",
    "  2\nTABLES",
    "  0\nTABLE",
    "  2\nLAYER",
    ` 70\n${Object.keys(layers).length}`,
    layerEntries,
    "  0\nENDTAB",
    "  0\nENDSEC",
  ].join("\n");
}

function lineEntity(x1, y1, x2, y2, layer) {
  return [
    "  0\nLINE",
    `  8\n${esc(layer)}`,
    ` 10\n${mm2unit(x1)}`,
    ` 20\n${mm2unit(-y1)}`,  // DXF Y is up; we flip
    ` 30\n0.0000`,
    ` 11\n${mm2unit(x2)}`,
    ` 21\n${mm2unit(-y2)}`,
    ` 31\n0.0000`,
  ].join("\n");
}

function textEntity(x, y, text, height, layer) {
  return [
    "  0\nTEXT",
    `  8\n${esc(layer)}`,
    ` 10\n${mm2unit(x)}`,
    ` 20\n${mm2unit(-y)}`,
    ` 30\n0.0000`,
    ` 40\n${mm2unit(height || 200)}`,
    `  1\n${esc(text)}`,
  ].join("\n");
}

function dimEntity(x1, y1, x2, y2, offset, text, layer) {
  // Simple aligned dimension using LINE + TEXT approximation
  // (DXF R12 DIMENSION entity is complex — use lines for portability)
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dist = Math.round(Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2));
  const label = text || `${dist}`;
  return textEntity(mx, my - (offset || 200), label, 150, layer);
}

// ─── Main export function ─────────────────────────────────────────────────────

/**
 * exportDXF(st) → string
 *
 * Converts the full DCEL state to a DXF R12 string.
 * Returns the DXF content as a string — caller handles download.
 */
export function exportDXF(st) {
  const layers = st.layers || {};
  const entities = [];

  // ── Walls (edges) ──────────────────────────────────────────────────────────
  for (const edge of Object.values(st.edges || {})) {
    const ab = st.halfEdges[edge.halfEdgeA];
    const ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId];
    const nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;

    // Draw wall as two parallel lines (the actual wall thickness)
    const dx = nB.x - nA.x, dy = nB.y - nA.y;
    const L = Math.sqrt(dx * dx + dy * dy);
    if (L < 1) continue;
    const t = (edge.thickness || 230) / 2;
    const nx = -dy / L, ny = dx / L;

    entities.push(lineEntity(
      nA.x + nx * t, nA.y + ny * t,
      nB.x + nx * t, nB.y + ny * t,
      "walls"
    ));
    entities.push(lineEntity(
      nA.x - nx * t, nA.y - ny * t,
      nB.x - nx * t, nB.y - ny * t,
      "walls"
    ));
    // End caps
    entities.push(lineEntity(
      nA.x + nx * t, nA.y + ny * t,
      nA.x - nx * t, nA.y - ny * t,
      "walls"
    ));
    entities.push(lineEntity(
      nB.x + nx * t, nB.y + ny * t,
      nB.x - nx * t, nB.y - ny * t,
      "walls"
    ));
  }

  // ── Annotations ────────────────────────────────────────────────────────────
  for (const ann of Object.values(st.annotations || {})) {
    entities.push(textEntity(ann.x, ann.y, ann.text, 200, ann.layerId || "annotations"));
  }

  // ── Manual dimensions ──────────────────────────────────────────────────────
  for (const dim of Object.values(st.manualDims || {})) {
    entities.push(dimEntity(
      dim.ptA.x, dim.ptA.y,
      dim.ptB.x, dim.ptB.y,
      dim.offset || 200,
      dim.text,
      "dimensions"
    ));
    // Dim line
    entities.push(lineEntity(
      dim.ptA.x, dim.ptA.y - (dim.offset || 200),
      dim.ptB.x, dim.ptB.y - (dim.offset || 200),
      "dimensions"
    ));
    // Extension lines
    entities.push(lineEntity(dim.ptA.x, dim.ptA.y, dim.ptA.x, dim.ptA.y - (dim.offset || 200) - 50, "dimensions"));
    entities.push(lineEntity(dim.ptB.x, dim.ptB.y, dim.ptB.x, dim.ptB.y - (dim.offset || 200) - 50, "dimensions"));
  }

  // ── Grid lines ─────────────────────────────────────────────────────────────
  for (const gl of Object.values(st.gridLines || {})) {
    const EXTENT = 50000; // 50m grid extent
    if (gl.axis === "x") {
      entities.push(lineEntity(gl.position, -EXTENT, gl.position, EXTENT, "structural"));
      entities.push(textEntity(gl.position, -EXTENT - 500, gl.label, 300, "structural"));
    } else {
      entities.push(lineEntity(-EXTENT, gl.position, EXTENT, gl.position, "structural"));
      entities.push(textEntity(-EXTENT - 500, gl.position, gl.label, 300, "structural"));
    }
  }

  // ── Symbols (as point + text label) ────────────────────────────────────────
  for (const sym of Object.values(st.symbols || {})) {
    entities.push(textEntity(sym.x, sym.y, sym.type + (sym.label ? ` (${sym.label})` : ""), 150, sym.layerId || "annotations"));
  }

  // ── Room labels ────────────────────────────────────────────────────────────
  for (const [faceId, room] of Object.entries(st.rooms || {})) {
    const face = st.faces?.[faceId];
    if (face?.centroid) {
      const label = room.name || room.type || "Room";
      entities.push(textEntity(face.centroid.x, face.centroid.y, label, 200, "rooms"));
    }
  }

  // ── Assemble DXF ──────────────────────────────────────────────────────────
  const entitiesSection = [
    "  0\nSECTION",
    "  2\nENTITIES",
    ...entities,
    "  0\nENDSEC",
  ].join("\n");

  return [
    headerSection(),
    tablesSection(layers),
    entitiesSection,
    "  0\nEOF",
  ].join("\n");
}

/**
 * downloadDXF(st, filename) — trigger browser download
 */
export function downloadDXF(st, filename = "dpl-jade-export.dxf") {
  const content = exportDXF(st);
  const blob = new Blob([content], { type: "application/dxf" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Primitive exports (circles, arcs, polylines) ─────────────────────────────

function circleEntity(c, layerId) {
  return [
    "  0\nCIRCLE",
    `  8\n${layerId || "0"}`,
    `  10\n${mm2unit(c.cx)}`,
    `  20\n${mm2unit(c.cy)}`,
    `  30\n0.0000`,
    `  40\n${mm2unit(c.r)}`,
  ].join("\n");
}

function arcEntity(a, layerId) {
  const startDeg = (a.startAngle * 180 / Math.PI + 360) % 360;
  const endDeg   = (a.endAngle   * 180 / Math.PI + 360) % 360;
  return [
    "  0\nARC",
    `  8\n${layerId || "0"}`,
    `  10\n${mm2unit(a.cx)}`,
    `  20\n${mm2unit(a.cy)}`,
    `  30\n0.0000`,
    `  40\n${mm2unit(a.r)}`,
    `  50\n${startDeg.toFixed(4)}`,
    `  51\n${endDeg.toFixed(4)}`,
  ].join("\n");
}

function lwpolylineEntity(pts, closed, layerId) {
  const flag = closed ? 1 : 0;
  const lines = [
    "  0\nLWPOLYLINE",
    `  8\n${layerId || "0"}`,
    `  90\n${pts.length}`,
    `  70\n${flag}`,
  ];
  for (const p of pts) {
    lines.push(`  10\n${mm2unit(p.x)}`);
    lines.push(`  20\n${mm2unit(p.y)}`);
  }
  return lines.join("\n");
}

/** Monkey-patch the main exportDXF to include primitives */
const _origExport = typeof exportDXF !== "undefined" ? exportDXF : null;

export function exportDXFWithPrimitives(st) {
  // Build entities section including primitives
  const entities = [];

  // Original wall/annotation entities
  for (const edge of Object.values(st.edges || {})) {
    const ab = st.halfEdges[edge.halfEdgeA], ba = st.halfEdges[edge.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;
    entities.push([
      "  0\nLINE",
      `  8\n${edge.layerId || "walls"}`,
      `  10\n${mm2unit(nA.x)}`, `  20\n${mm2unit(nA.y)}`, `  30\n0.0000`,
      `  11\n${mm2unit(nB.x)}`, `  21\n${mm2unit(nB.y)}`, `  31\n0.0000`,
    ].join("\n"));
  }

  // Circles
  for (const c of Object.values(st.circles || {})) {
    entities.push(circleEntity(c, c.layerId));
  }

  // Arcs
  for (const a of Object.values(st.arcs || {})) {
    entities.push(arcEntity(a, a.layerId));
  }

  // Polylines (LWPOLYLINE)
  for (const pl of Object.values(st.polylines || {})) {
    if (pl.pts?.length >= 2) {
      entities.push(lwpolylineEntity(pl.pts, pl.closed, pl.layerId));
    }
  }

  // Annotations
  for (const ann of Object.values(st.annotations || {})) {
    entities.push([
      "  0\nTEXT",
      `  8\nannotations`,
      `  10\n${mm2unit(ann.x)}`, `  20\n${mm2unit(ann.y)}`, `  30\n0.0000`,
      `  40\n0.2500`,
      `  1\n${esc(ann.text || "")}`,
    ].join("\n"));
  }

  const header = [
    "  0\nSECTION", "  2\nHEADER",
    "  9\n$ACADVER", "  1\nAC1009",
    "  9\n$INSUNITS", " 70\n4",
    "  0\nENDSEC",
    "  0\nSECTION", "  2\nENTITIES",
  ].join("\n");

  const footer = ["  0\nENDSEC", "  0\nEOF"].join("\n");

  return [header, ...entities, footer].join("\n");
}
